import { useCallback, useEffect, useRef, useState } from 'react';
import type { FetchSharePage, ShareHistoryQuery, ShareHistoryRow } from './types';

// 공용 무한스크롤 목록 훅 — work-log ShareHistoryPage 의 page/size + IntersectionObserver
// 패턴을 추출해 3메뉴가 공유한다. 공용 조각 중 유일하게 없던 "무한스크롤"을 여기서 메운다.
// 근거: 공유이력_일관화_개발설계서 §3.2.

export type ListLoadState = 'loading' | 'ready' | 'error';

/** page/size 를 제외한 조회 질의(훅이 page 를 소유) */
export type InfiniteQuery = Omit<ShareHistoryQuery, 'page' | 'size'>;

export interface UseInfiniteListResult {
  items: ShareHistoryRow[];
  state: ListLoadState;
  hasNext: boolean;
  loadingMore: boolean;
  /** 스크롤 컨테이너 ref(IntersectionObserver root) */
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  /** 목록 끝 sentinel ref */
  sentinelRef: React.MutableRefObject<HTMLDivElement | null>;
  /** 첫 페이지부터 재조회(오류 재시도용) */
  reload: () => void;
}

export function useInfiniteList(
  fetchPage: FetchSharePage,
  query: InfiniteQuery,
  pageSize: number,
): UseInfiniteListResult {
  const [items, setItems] = useState<ShareHistoryRow[]>([]);
  const [hasNext, setHasNext] = useState(false);
  const [state, setState] = useState<ListLoadState>('loading');
  const [loadingMore, setLoadingMore] = useState(false);

  const pageRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // fetchPage·query 는 렌더마다 새 참조일 수 있어 ref 로 고정하고,
  // 재조회 트리거는 질의 내용 변화(JSON key)로만 건다.
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  const queryRef = useRef(query);
  queryRef.current = query;
  const queryKey = JSON.stringify(query);

  const fetchList = useCallback(
    (opts: { append: boolean }, signal?: AbortSignal) => {
      const page = opts.append ? pageRef.current + 1 : 0;
      return fetchRef
        .current({ ...queryRef.current, page, size: pageSize }, signal)
        .then((res) => {
          setItems((prev) => (opts.append ? [...prev, ...res.items] : res.items));
          setHasNext(res.hasNext);
          pageRef.current = page;
          setState('ready');
        });
    },
    [pageSize],
  );

  // 질의 변경 시 첫 페이지부터 재조회
  useEffect(() => {
    const controller = new AbortController();
    setState('loading');
    fetchList({ append: false }, controller.signal).catch((err) => {
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
      setState('error');
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, fetchList]);

  const loadMore = useCallback(() => {
    if (!hasNext || loadingMore) return;
    setLoadingMore(true);
    fetchList({ append: true })
      .catch(() => {
        /* 추가 조회 실패는 조용히 무시 — 기존 목록 유지 */
      })
      .finally(() => setLoadingMore(false));
  }, [hasNext, loadingMore, fetchList]);

  // sentinel 교차 시 다음 페이지
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || state !== 'ready' || !hasNext || loadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root: scrollRef.current, rootMargin: '300px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [state, hasNext, loadingMore, loadMore]);

  const reload = useCallback(() => {
    setState('loading');
    fetchList({ append: false }).catch(() => setState('error'));
  }, [fetchList]);

  return { items, state, hasNext, loadingMore, scrollRef, sentinelRef, reload };
}
